import { createAsyncThunk } from '@reduxjs/toolkit';

import { PRIMARY_GREEN, PRIMARY_RED } from 'commons/styles/colors';
import { defaultActionProcess } from 'commons/utils/defaultActionProcess';
import {
  setBankAccounts,
  setPostingPeriods,
  setAllInventoryAdjustments,
  updateSelectedInventoryAdjustmentItem,
  setIAActiveLocations,
  setIAItemListTableError,
  setSelectedInventoryAdjustment,
} from 'redux-stores/reducers/inventoryAdjustment.reducer';
import { snackbarSetData } from 'redux-stores/reducers/utilityReducer';
import { AppDispatch } from 'redux-stores/store';
import { swiperxWmsApiInventoryAdjustment } from 'services/apis/SwipeRxWmsApiInventoryAdjustment';
import {
  IGetInventoryAdjustmentListResponseDTO,
  IInventoryAdjustmentBinRequest,
  IInventoryAdjustmentBinResponse,
  IInventoryAdjustmentItemRequest,
  IInventoryAdjustmentProductDetailRequest,
  IInventoryAdjustmentProductDetailResponse,
  IInventoryAdjustmentProductInfoRequest,
  IInventoryAdjustmentProductInfoResponse,
  IInventoryAdjustmentRemoveItemRequest,
  UpdateInventoryAdjustmentInfoRequest,
} from 'services/apis/SwipeRxWmsApiInventoryAdjustment/types';
import { IGetInventoryAdjustmentActiveLocations } from 'services/apis/SwipeRxWmsApiInventoryAdjustment/types/get-inventory-adjustment-active-locations.type';

export const getActivePostingPeriodsAction = createAsyncThunk(
  'inventoryAdjustment/getActivePostingPeriodsAction',
  async (_, { dispatch }) => {
    defaultActionProcess(async () => {
      const data =
        await swiperxWmsApiInventoryAdjustment.getActivePostingPeriods();
      dispatch(setPostingPeriods(data));
    }, dispatch);
    return undefined;
  },
);

export const getActiveBankAccountsAction = createAsyncThunk(
  'inventoryAdjustment/getActiveBankAccountsAction',
  async (_, { dispatch }) => {
    defaultActionProcess(async () => {
      const data =
        await swiperxWmsApiInventoryAdjustment.getActiveBankAccounts();
      dispatch(setBankAccounts(data));
    }, dispatch);
    return undefined;
  },
);

export const updateInventoryAdjustmentInfo = createAsyncThunk(
  'inventoryAdjustment/updateInventoryAdjustmentInfo',
  async (
    {
      documentNumber,
      params,
    }: { documentNumber: string; params: UpdateInventoryAdjustmentInfoRequest },
    { dispatch },
  ) => {
    await defaultActionProcess(async () => {
      const response =
        await swiperxWmsApiInventoryAdjustment.updateInventoryAdjustmentInfo(
          documentNumber,
          params,
        );
      if (response.error) {
        const errorMessage =
          response.error === true
            ? 'Failed to update inventory adjustment info. Please make sure Adjustment Account, Transaction Date, & Posting Period is correct.'
            : response.error;
        throw new Error(errorMessage);
      }
      // save changes to state
      dispatch(setSelectedInventoryAdjustment(response.data));
    }, dispatch);
  },
);

export const getInventoryAdjustmentProductInfo = async (
  params: IInventoryAdjustmentProductInfoRequest,
  dispatch: AppDispatch,
): Promise<IInventoryAdjustmentProductInfoResponse | null> => {
  const response =
    await swiperxWmsApiInventoryAdjustment.getProductInfo(params);
  if (response && response.data) {
    return response.data;
  }
  if (response.error) {
    const errorMessage =
      response.error === true
        ? 'Failed to get product detail. Please make sure SKU, Bin, and Inventory Number is correct.'
        : response.error;
    dispatch(
      snackbarSetData({
        open: true,
        message: errorMessage,
        color: PRIMARY_RED,
      }),
    );
  }
  return null;
};

export const getInventoryAdjustmentProductDetail = async (
  params: IInventoryAdjustmentProductDetailRequest,
  qtyAdjustment: number,
  dispatch: AppDispatch,
): Promise<IInventoryAdjustmentProductDetailResponse | null> => {
  const response =
    await swiperxWmsApiInventoryAdjustment.getProductDetail(params);
  if (response && response.data) {
    return response.data;
  }
  if (response.error) {
    const errorMessage =
      response.error === true
        ? 'Failed to get product detail. Please make sure SKU, Bin, Inventory Number, and Location is correct.'
        : response.error;
    if (errorMessage && qtyAdjustment < 0) {
      dispatch(
        snackbarSetData({
          open: true,
          message: errorMessage,
          color: PRIMARY_RED,
        }),
      );
    }
  }
  return null;
};

export const getInventoryAdjustmentBin = async (
  params: IInventoryAdjustmentBinRequest,
  dispatch: AppDispatch,
): Promise<IInventoryAdjustmentBinResponse | null> => {
  const response = await swiperxWmsApiInventoryAdjustment.getBin(params);
  if (response && response.data) {
    return response.data;
  }
  if (response.error) {
    const errorMessage =
      response.error === true
        ? 'Failed to get bin. Please make sure Bin name and Location is correct.'
        : response.error;
    if (errorMessage) {
      dispatch(
        snackbarSetData({
          open: true,
          message: errorMessage,
          color: PRIMARY_RED,
        }),
      );
    }
  }
  return null;
};

export const postInventoryAdjustmentItem = async (
  params: IInventoryAdjustmentItemRequest,
  dispatch: AppDispatch,
): Promise<void> => {
  const response =
    await swiperxWmsApiInventoryAdjustment.postInventoryAdjustmentItem(params);

  if (response.error) {
    const errorMessage =
      response.error === true
        ? 'Failed to update inventory adjustment item. Please make sure SKU, Bin, Inventory Number, and Quantity is correct.'
        : response.error;
    dispatch(
      snackbarSetData({
        open: true,
        message: errorMessage,
        color: PRIMARY_RED,
      }),
    );
  } else {
    dispatch(updateSelectedInventoryAdjustmentItem(response.data.items));
  }
};

export const postInventoryAdjustmentRemoveItem = async (
  params: IInventoryAdjustmentRemoveItemRequest,
): Promise<void> => {
  await swiperxWmsApiInventoryAdjustment.postRemoveInventoryAdjustmentItem(
    params,
  );
};

export const postCompleteInventoryAdjustment = createAsyncThunk(
  'inventoryAdjustment/postCompleteInventoryAdjustment',
  async (
    params: { documentNumber: string; next?: () => void },
    { dispatch },
  ) => {
    return defaultActionProcess(async () => {
      const tableDataError =
        await swiperxWmsApiInventoryAdjustment.postCompleteInventoryAdjustment(
          params.documentNumber,
        );
      if (tableDataError) {
        // set to tableDataError state
        dispatch(setIAItemListTableError(tableDataError));
        throw new Error('Data issue error. Please check your data');
      }
      // complete inventory adjustment success
      dispatch(
        snackbarSetData({
          open: true,
          message: 'Inventory Adjustment Processed',
          color: PRIMARY_GREEN,
        }),
      );
      if (params.next) params.next();
    }, dispatch);
  },
);

export const removeInventoryAdjustment = createAsyncThunk(
  'inventoryAdjustment/removeInventoryAdjustment',
  async (documentNumber: string, { dispatch }) => {
    await defaultActionProcess(async () => {
      await swiperxWmsApiInventoryAdjustment.removeInventoryAdjustment(
        documentNumber,
      );
    }, dispatch);
  },
);

export const getInventoryAdjustments = createAsyncThunk(
  'inventoryAdjustment/getInventoryAdjustments',
  async (_, { dispatch }) => {
    defaultActionProcess(async () => {
      const response: IGetInventoryAdjustmentListResponseDTO =
        await swiperxWmsApiInventoryAdjustment.getInventoryAdjustments();
      dispatch(setAllInventoryAdjustments(response.data));
    }, dispatch);
  },
);

export const getInventoryAdjustmentActiveLocations = createAsyncThunk(
  'inventoryAdjustment/getInventoryAdjustmentActiveLocations',
  async (_, { dispatch }) => {
    defaultActionProcess(async () => {
      const data: IGetInventoryAdjustmentActiveLocations[] =
        await swiperxWmsApiInventoryAdjustment.getActiveLocations();
      dispatch(setIAActiveLocations(data));
    }, dispatch);
  },
);

export const createInventoryAdjustment = createAsyncThunk(
  'inventoryAdjustment/createInventoryAdjustment',
  async (_, { dispatch }) => {
    await defaultActionProcess(async () => {
      await swiperxWmsApiInventoryAdjustment.postNewInventoryAdjustment();
    }, dispatch);
  },
);
